/**
 * EnterpriseSceneManager - Clean Scene Architecture
 * Separates scene presentation from logic for clean mobile/desktop boundaries
 * Prevents scene architecture violations and mixed mobile/desktop presentation
 * Enterprise-grade scene management with platform-specific presentation layers
 * CRITICAL: Provides separation of concerns - handles ONLY scene presentation logic
 */

// Dynamic imports to avoid module loading issues
let MobileDetectionService, desktopStyleManager, mobileStyleManager;

export class EnterpriseSceneManager {
    constructor() {
        this.currentScene = null;
        this.sceneRegistry = new Map();
        this.presentationLayers = new Map();
        this.sceneTransitions = new Map();
        
        console.log('🏢 EnterpriseSceneManager initialized - SEPARATE from MobileDesktopRouter');
        
        // CRITICAL: Immediate platform detection and initialization
        // Don't wait for dependencies - initialize immediately
        this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        this.initializePlatformSpecificManagers();
        
        // Then enhance with proper dependencies when available
        this.loadDependencies().then(() => {
            const properDetection = MobileDetectionService?.isMobile() || false;
            if (properDetection !== this.isMobile) {
                console.log(`🔄 Updating platform detection: ${this.isMobile} → ${properDetection}`);
                this.isMobile = properDetection;
                this.initializePlatformSpecificManagers();
            }
        });
    }

    /**
     * Load dependencies dynamically
     */
    async loadDependencies() {
        try {
            // Load MobileDetectionService
            if (window.MobileDetectionService) {
                MobileDetectionService = window.MobileDetectionService;
            } else {
                const mobileModule = await import('./MobileDetectionService.js');
                MobileDetectionService = mobileModule.default;
            }
            
            // Use global style managers if available
            desktopStyleManager = window.desktopStyleManager;
            mobileStyleManager = window.mobileStyleManager;
            
            this.isMobile = MobileDetectionService?.isMobile() || false;
            
        } catch (error) {
            console.warn('⚠️ Some dependencies failed to load:', error.message);
            // Fallback detection
            this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        }
    }

    /**
     * Initialize platform-specific managers
     * CRITICAL: This is SEPARATE from routing - handles presentation layer only
     */
    initializePlatformSpecificManagers() {
        if (this.isMobile) {
            console.log('📱 Initializing mobile presentation layer (separate from UnifiedMobileSystem)');
            // Mobile-specific presentation initialization
        } else {
            console.log('🖥️ Initializing desktop presentation layer');
            // Desktop-specific presentation initialization
        }
    }

    /**
     * Register a scene with platform-specific presentation
     * CRITICAL: Separation of concerns - presentation logic separate from business logic
     */
    registerScene(sceneKey, sceneConfig) {
        console.log(`🏢 Registering scene presentation: ${sceneKey}`);
        
        const enterpriseSceneConfig = {
            key: sceneKey,
            businessLogic: sceneConfig.businessLogic || null,
            desktopPresentation: sceneConfig.desktopPresentation || null,
            mobilePresentation: sceneConfig.mobilePresentation || null,
            sharedLogic: sceneConfig.sharedLogic || null,
            platformAgnostic: sceneConfig.platformAgnostic || false
        };
        
        this.sceneRegistry.set(sceneKey, enterpriseSceneConfig);
        console.log(`✅ Scene presentation registered: ${sceneKey}`);
    }

    /**
     * Create platform-specific scene presentation
     * CRITICAL: This handles ONLY presentation layer, not routing
     */
    createScenePresentation(sceneKey, sceneInstance) {
        const sceneConfig = this.sceneRegistry.get(sceneKey);
        if (!sceneConfig) {
            console.warn(`⚠️ Scene not registered: ${sceneKey}`);
            return this.createDefaultPresentation(sceneInstance);
        }
        
        console.log(`🎨 Creating ${this.isMobile ? 'mobile' : 'desktop'} presentation for: ${sceneKey}`);
        
        if (this.isMobile) {
            return this.createMobilePresentation(sceneConfig, sceneInstance);
        } else {
            return this.createDesktopPresentation(sceneConfig, sceneInstance);
        }
    }

    /**
     * Create mobile-specific presentation layer
     * CRITICAL: Handles ONLY presentation, not mobile system functionality
     */
    createMobilePresentation(sceneConfig, sceneInstance) {
        const presentation = {
            platform: 'mobile',
            sceneKey: sceneConfig.key,
            
            // Mobile-specific UI presentation setup
            setupUI: () => {
                console.log('📱 Setting up mobile presentation layer');
                
                // Apply mobile-specific presentation styles
                this.applyMobilePresentationStyles(sceneConfig.key);
                
                // Setup mobile presentation navigation
                this.setupMobilePresentationNavigation(sceneInstance);
                
                // Execute mobile presentation logic
                if (sceneConfig.mobilePresentation) {
                    sceneConfig.mobilePresentation.call(sceneInstance);
                }
            },
            
            // Mobile-specific cleanup
            cleanup: () => {
                console.log('📱 Cleaning up mobile presentation');
                this.cleanupMobilePresentation(sceneConfig.key);
            }
        };
        
        this.presentationLayers.set(sceneConfig.key, presentation);
        return presentation;
    }

    /**
     * Create desktop-specific presentation layer
     * CRITICAL: Handles ONLY presentation, separate from business logic
     */
    createDesktopPresentation(sceneConfig, sceneInstance) {
        const presentation = {
            platform: 'desktop',
            sceneKey: sceneConfig.key,
            
            // Desktop-specific UI presentation setup
            setupUI: () => {
                console.log('🖥️ Setting up desktop presentation layer');
                
                // Apply desktop-specific presentation styles
                this.applyDesktopPresentationStyles(sceneConfig.key);
                
                // Setup desktop presentation navigation
                this.setupDesktopPresentationNavigation(sceneInstance);
                
                // Execute desktop presentation logic
                if (sceneConfig.desktopPresentation) {
                    sceneConfig.desktopPresentation.call(sceneInstance);
                }
            },
            
            // Desktop-specific cleanup
            cleanup: () => {
                console.log('🖥️ Cleaning up desktop presentation');
                this.cleanupDesktopPresentation(sceneConfig.key);
            }
        };
        
        this.presentationLayers.set(sceneConfig.key, presentation);
        return presentation;
    }

    /**
     * Apply mobile-specific presentation styles
     * CRITICAL: ONLY presentation styles, not system-level mobile functionality
     */
    applyMobilePresentationStyles(sceneKey) {
        const mobilePresentationStyles = `
            /* Mobile Presentation Styles - ${sceneKey} */
            /* SEPARATE from UnifiedMobileSystem - presentation layer only */
            .scene-${sceneKey} {
                /* Mobile-specific scene presentation styling */
                --scene-mobile-padding: var(--mobile-safe-area-top, 0px) 
                                       var(--mobile-safe-area-right, 0px) 
                                       var(--mobile-safe-area-bottom, 0px) 
                                       var(--mobile-safe-area-left, 0px);
            }
            
            .scene-${sceneKey} .mobile-nav {
                display: block !important;
                position: fixed;
                bottom: var(--mobile-safe-area-bottom, 0px);
                left: var(--mobile-safe-area-left, 0px);
                right: var(--mobile-safe-area-right, 0px);
                z-index: 1000;
                background: rgba(15, 23, 42, 0.95);
                backdrop-filter: blur(20px);
            }
            
            .scene-${sceneKey} .desktop-nav {
                display: none !important;
            }
            
            .scene-${sceneKey} .content-area {
                padding: var(--scene-mobile-padding);
            }
        `;
        
        mobileStyleManager?.injectStyles(`scene-${sceneKey}-presentation-mobile`, mobilePresentationStyles);
    }

    /**
     * Apply desktop-specific presentation styles
     */
    applyDesktopPresentationStyles(sceneKey) {
        const desktopPresentationStyles = `
            /* Desktop Presentation Styles - ${sceneKey} */
            .scene-${sceneKey} {
                /* Desktop-specific scene presentation styling */
                position: relative;
                padding: 0;
            }
            
            .scene-${sceneKey} .desktop-nav {
                display: block !important;
            }
            
            .scene-${sceneKey} .mobile-nav {
                display: none !important;
            }
            
            .scene-${sceneKey} .content-area {
                padding: 0; /* No safe area padding on desktop */
            }
        `;
        
        desktopStyleManager?.injectStyles(`scene-${sceneKey}-presentation-desktop`, desktopPresentationStyles);
    }

    /**
     * Setup mobile presentation navigation
     * CRITICAL: ONLY presentation layer, not system functionality
     */
    setupMobilePresentationNavigation(sceneInstance) {
        // Mobile-specific presentation navigation setup
        if (sceneInstance.setRibbonVisibility) {
            sceneInstance.setRibbonVisibility(false); // Hide desktop ribbon on mobile
        }
        
        // Add mobile presentation class to scene
        if (sceneInstance.sys && sceneInstance.sys.canvas) {
            const gameContainer = document.getElementById('game-container');
            if (gameContainer) {
                gameContainer.classList.add('mobile-presentation-active');
            }
        }
    }

    /**
     * Setup desktop presentation navigation
     */
    setupDesktopPresentationNavigation(sceneInstance) {
        // Desktop-specific presentation navigation setup
        if (sceneInstance.setRibbonVisibility) {
            sceneInstance.setRibbonVisibility(true); // Show desktop ribbon
        }
        
        // Add desktop presentation class to scene
        if (sceneInstance.sys && sceneInstance.sys.canvas) {
            const gameContainer = document.getElementById('game-container');
            if (gameContainer) {
                gameContainer.classList.add('desktop-presentation-active');
            }
        }
    }

    /**
     * Get current platform
     */
    getCurrentPlatform() {
        return this.isMobile ? 'mobile' : 'desktop';
    }

    /**
     * Get presentation layer status
     */
    getStatus() {
        return {
            platform: this.getCurrentPlatform(),
            registeredScenes: Array.from(this.sceneRegistry.keys()),
            activePresentations: Array.from(this.presentationLayers.keys()),
            recentTransitions: Array.from(this.sceneTransitions.entries()).slice(-5),
            separateFromRouting: true // This is ONLY presentation, not routing
        };
    }

    /**
     * Cleanup mobile presentation
     */
    cleanupMobilePresentation(sceneKey) {
        // Remove mobile presentation classes
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.classList.remove('mobile-presentation-active');
        }
        
        // Remove mobile presentation styles
        const mobileStyles = document.getElementById(`scene-${sceneKey}-presentation-mobile`);
        if (mobileStyles) {
            mobileStyles.remove();
        }
    }

    /**
     * Cleanup desktop presentation
     */
    cleanupDesktopPresentation(sceneKey) {
        // Remove desktop presentation classes
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.classList.remove('desktop-presentation-active');
        }
        
        // Remove desktop presentation styles
        const desktopStyles = document.getElementById(`scene-${sceneKey}-presentation-desktop`);
        if (desktopStyles) {
            desktopStyles.remove();
        }
    }

    /**
     * Cleanup all resources
     */
    cleanup() {
        // Cleanup all presentation layers
        this.presentationLayers.forEach((presentation, sceneKey) => {
            if (presentation.cleanup) {
                presentation.cleanup();
            }
        });
        
        // Clear all registries
        this.sceneRegistry.clear();
        this.presentationLayers.clear();
        this.sceneTransitions.clear();
        
        console.log('🧹 EnterpriseSceneManager cleaned up');
    }
}

// Create singleton instance
const enterpriseSceneManager = new EnterpriseSceneManager();

// Make globally available for integration
window.enterpriseSceneManager = enterpriseSceneManager;

export default enterpriseSceneManager;
